import { PageBase } from "app/components/Common/PageBase";
import {
  Box,
  Card,
  Stack,
  Typography,
  Button,
  CircularProgress,
  CardContent,
  Alert,
  Link,
  Divider,
} from "@mui/joy";
import { PageBodyContainer } from "app/components/Common/PageBodyContainer";
import { CustomDimensions } from "styles/theme/themes";
import { getSubjectIcon, getSubjectColor, getPaperColor } from "app/utils";

import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import ExamStatus from "app/components/ExamStatus/ExamStatus";
import useSubmission from "./utils/useSubmission";
import CardExplanation from "app/components/CardExplanation/CardExplanation";
import ChatBotAlert from "app/components/ChatBotAlert/ChatBotAlert";
import { DOCUMENTATION_ROUTE } from "app/constants/route";
import { getTextExamStatus } from "app/components/ExamStatus/utils/useExamStatus";
import SubmissionPanel, {
  TypeSubmissionPanelProps,
} from "app/components/SubmissionPanel/SubmissionPanel";
import CardInfoSubmission from "app/components/CardInfoSubmission/CardInfoSubmission";
import SubmissionDetailsCard from "app/components/SubmissionDetailsCard/SubmissionDetailsCard";
import QuestionPaperCard from "app/components/QuestionPaperCard/QuestionPaperCard";
import MarkingSchemeCard from "app/components/MarkingSchemeCard/MarkingSchemeCard";
import useStyleStudentAssignmentSubmissionPage from "./utils/useStyleStudentAssignmentSubmissionPage";
import LoadingCircular from "app/components/LoadingCircular/LoadingCircular";
import ModalConfirm from "app/components/ModalConfirm/ModalConfirm";

export function StudentAssignmentSubmissionPage() {
  const states = useSubmission();
  const {
    examId,
    exam,
    loading,
    error,
    currentUserSubmission,
    hasSubmission,
    isDueDatePassed,
    submissionStatus,
    isPolling,
    examPolling,
    returnedPapers,
    explanationVideos,
  } = states;

  if (exam?.status === "standby-locked") {
    return (
      <PageBase breadcrumbs={[{ text: "Submission" }]}>
        <PageBodyContainer
          size="xl"
          sx={{
            overflow: "auto",
            maxWidth: "unset",
            p: { xs: 1, md: 4 },
            boxSizing: "border-box",
            zoom: { xs: "70%", sm: "100%" },
          }}
        >
          <Stack
            direction="column"
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: "60vh" }}
          >
            <img
              src={require("assets/illustration-clock.svg")?.default}
              style={{
                filter: "grayscale(1) contrast(0.5) brightness(1.3)",
                opacity: 0.7,
                width: "100%",
                maxWidth: "300px",
                height: "auto",
              }}
            />
            <Typography textAlign="center">
              The exam is locked and will be available on
              <br /> <b>{exam?.examTime}</b>
            </Typography>
          </Stack>
        </PageBodyContainer>
      </PageBase>
    );
  }

  const style = useStyleStudentAssignmentSubmissionPage({exam: exam});

  return (
    <PageBase breadcrumbs={[{ text: "Submission" }]}>
      <PageBodyContainer
        size="xl"
        sx={{
          overflow: "auto",
          maxWidth: "unset",
          p: { xs: 1, md: 4 },
          boxSizing: "border-box",
          zoom: { xs: "70%", sm: "100%" },
        }}
      >
        <Stack
          sx={{
            maxWidth: 1800,
            alignSelf: "center",
            boxSizing: "border-box",
            px: { xs: 1, sm: 2, md: 3 }, // Responsive padding
          }}
        >
          {loading && !exam && <LoadingCircular />}
          {!loading && error && (
            <Stack
              direction="column"
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{ minHeight: "60vh" }}
            >
              <WarningRoundedIcon
                sx={{ fontSize: 48, color: "warning.main" }}
              />
              <Typography level="body-lg" color="warning">
                Error
              </Typography>
            </Stack>
          )}
          {!loading && !error && exam && (
            <>
              <ChatBotAlert
                theme={exam?.status == "graded" ? "yellow" : "default"}
                status={exam?.status == "graded" ? "feedback" : exam.status}
                examId={examId}
                useStatic={exam?.status == "graded"}
              />
              <Stack
                direction={{ xs: "column", sm: "row" }} // Stack vertically on small screens
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                sx={{
                  mb: 3,
                }}
                justifyContent={"space-between"}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: { xs: 2, sm: 0 } }} // Add margin bottom on small screens
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: CustomDimensions.cornerRadius,
                      backgroundColor: getSubjectColor(exam.subject)[100],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {getSubjectIcon(exam.subject, 36)}
                  </Box>
                  <Stack spacing={0.5}>
                    <Typography level="h2">{exam.title}</Typography>
                    {exam.level && (
                      <Typography level="title-md" color="neutral">
                        Level: {exam.level}
                      </Typography>
                    )}
                    {exam.description && (
                      <Typography level="title-md" color="neutral" textTransform={'capitalize'}>
                        Paper: {exam.description}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
                <ExamStatus
                  type={exam.status as any}
                  data={{ examDate: exam.examTime }}
                />
              </Stack>

              {/* Instructions */}
             
              <Stack spacing={2} sx={{ mb: 2 }}>
              {exam.status != "ended" && exam.status != "graded"  && <Stack spacing={2}>
                
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  gap={1}
                  alignItems={"center"}
                >
                  <Typography level="body-md">
                    Make sure to read the exam <b>instructions</b> before you
                    begin.
                  </Typography>
                  <Button
                    component="a"
                    href={DOCUMENTATION_ROUTE}
                    target="_blank"
                    variant="outlined"
                    color="primary"
                  >
                    Check them here!
                  </Button>
                </Stack>
                <Alert
                  color="warning"
                  variant="soft"
                  sx={{ alignItems: "flex-start", mt: 1 }}
                  startDecorator={
                    <Box sx={{ mt: 0.5 }}>
                      <WarningRoundedIcon />
                    </Box>
                  }
                >
                  <Typography color="warning" level="body-md">
                    Please complete this examination independently and adhere
                    to the time constraints. Upholding academic integrity is
                    essential for a fair assessment environment and your own
                    improvement.
                  </Typography>
                </Alert>
              </Stack> }
                
                <Stack sx={style.cardDetails}>
                  <QuestionPaperCard exam={exam} />
                  <MarkingSchemeCard exam={exam} />
                  <SubmissionDetailsCard
                    exam={exam}
                    isPolling={isPolling}
                    examPolling={examPolling}
                    currentUserSubmission={currentUserSubmission}
                    submissionStatus={submissionStatus}
                  />
                </Stack>
              </Stack>

              <CardInfoSubmission
                exam={exam}
                isDueDatePassed={isDueDatePassed}
                hasSubmission={hasSubmission}
                currentUserSubmission={currentUserSubmission}
              />

              <SubmissionPanel
                {...((states || {}) as TypeSubmissionPanelProps)}
              />
              {(exam.status == "standby-open" || exam.status == "pending") && (
                <Card>
                  <CardContent
                    sx={{
                      justifyContent: "center",
                      alignItems: "center",
                      gap: { xs: 2, sm: 4 }, // Less gap on small screens
                      p: { xs: 2, sm: 4 }, // Less padding on small screens
                      textAlign: "center", // Center text on all screens
                    }}
                  >
                    {exam.status == "standby-open" && (
                      <>
                        <img
                          src={
                            require("assets/illustration-clock.svg")?.default
                          }
                          style={{
                            filter:
                              "grayscale(1) contrast(0.5) brightness(1.3)",
                            opacity: 0.7,
                            width: "100%", // Responsive width
                            maxWidth: "300px", // Maximum width
                            height: "auto", // Maintain aspect ratio
                          }}
                        />
                        <Typography textAlign={"center"}>
                          Please wait the exam will be held on
                          <br /> <b>{exam?.examTime}</b>
                        </Typography>
                        {exam?.questionPaperArtifactUri && (
                          <>
                            <Divider />
                            <Stack
                              sx={{
                                maxWidth: "450px",
                                textAlign: "center",
                                alignItems: "center",
                                gap: { xs: 2, sm: 4 },
                              }}
                            >
                              <Typography>
                                The exam question paper is now available for
                                download. Make sure to get your copy and start
                                preparing!
                              </Typography>
                              <Link
                                href={exam.questionPaperArtifactUri}
                                target="_blank"
                              >
                                📥 Download Now
                              </Link>
                            </Stack>
                          </>
                        )}
                      </>
                    )}
                    {exam.status == "pending" && (
                      <Stack
                        sx={{
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <img
                          src={
                            require("assets/illustration-analysis.svg")?.default
                          }
                          style={{
                            filter: "grayscale(1)",
                            opacity: 0.7,
                            width: "100%", // Responsive width
                            maxWidth: "300px", // Maximum width
                            height: "auto", // Maintain aspect ratio
                          }}
                        />
                        <Typography
                          textAlign={"center"}
                          level="h4"
                          sx={{ mt: 3 }}
                        >
                          📝 Submission Under Review
                        </Typography>
                        <Typography textAlign={"center"} maxWidth={800}>
                          Your submission is being checked. Please check back
                          later for the results.
                          <br /> We’ll notify you once the review is complete!
                          🚀
                        </Typography>
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              )}
              {exam.status == "graded" && (
                <CardExplanation
                  title={exam.title}
                  explanation={{
                    papers: returnedPapers,
                    videos: explanationVideos,
                  }}
                />
              )}
            </>
          )}
          {!loading && !error && !exam && examId && (
            <Stack
              direction="column"
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{ minHeight: "60vh" }}
            >
              <AssignmentLateIcon sx={{ fontSize: 48, color: "neutral.500" }} />
              <Typography level="body-lg" color="neutral">
                No exam found
              </Typography>
            </Stack>
          )}
        </Stack>
      </PageBodyContainer>
    </PageBase>
  );
}
